import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, from } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { User, UserRegistration, DoseType, UserDoseHistory, DosePurchaseEligibility, Product, OfflineDoseEntry } from '../interfaces/product.interface';
import {
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile as updateFirebaseProfile,
  User as FirebaseUser,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
  ConfirmationResult
} from '@angular/fire/auth';
import {
  Firestore,
  doc,
  setDoc,
  getDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  DocumentReference
} from '@angular/fire/firestore';
import { ProductService } from './product.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private redirectUrl: string = '/';
  private authStateReady = false;

  constructor(private auth: Auth, private firestore: Firestore, private productService: ProductService) {
    // Listen to Firebase auth state changes
    onAuthStateChanged(this.auth, async (firebaseUser) => {
      console.log('Firebase Auth State Changed:', firebaseUser ? 'User logged in' : 'User logged out');

      if (firebaseUser) {
        // User is signed in, get additional user data from Firestore
        const userData = await this.getUserData(firebaseUser.uid);
        if (userData) {
          console.log('Setting user data from Firebase:', userData.email);
          this.currentUserSubject.next(userData);
          // Cache user in local storage
          localStorage.setItem('currentUser', JSON.stringify(userData));
        } else {
          console.log('No user data found in Firestore, signing out');
          this.currentUserSubject.next(null);
          localStorage.removeItem('currentUser');
        }
      } else {
        // User is signed out
        console.log('User signed out, clearing state');
        this.currentUserSubject.next(null);
        localStorage.removeItem('currentUser');
      }
      
      // Mark auth state as ready
      this.authStateReady = true;
    });

    // Don't load cached user immediately - wait for Firebase auth state
    // This prevents the race condition where cached data shows user as logged in
    // before Firebase confirms the actual authentication state
  }

  // Method to wait for auth state to be ready
  waitForAuthState(): Promise<void> {
    return new Promise((resolve) => {
      if (this.authStateReady) {
        resolve();
      } else {
        const subscription = this.currentUser$.subscribe(() => {
          subscription.unsubscribe();
          resolve();
        });
      }
    });
  }

  // Get user data from Firestore
  private async getUserData(uid: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(this.firestore, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          id: uid,
          name: data['name'] || '',
          email: data['email'] || '',
          mobile: data['mobile'] || '',
          password: '', // Don't store password in Firestore
          gender: data['gender'] || 'other',
          currentWeight: data['currentWeight'] || 0,
          currentHeight: data['currentHeight'] || 0,
          dateOfBirth: data['dateOfBirth']?.toDate() || new Date(),
          fullAddress: data['fullAddress'] || '',
          altMobile: data['altMobile'] || '',
          registrationDate: data['registrationDate']?.toDate() || new Date(),
          recentPhoto: data['recentPhoto'] || '',
          role: data['role'] || 'user', // Default to 'user' if no role specified
          takenDose: data['takenDose'] || 0,
          completedDoses: data['completedDoses'] || [],
          currentDoseHistory: data['currentDoseHistory'] || []
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  }

  // Register new user with Firebase Auth and store data in Firestore
  register(userData: UserRegistration): Observable<{ success: boolean; message: string; user?: User }> {
    // First validate email and mobile uniqueness
    return this.checkEmailExists(userData.email).pipe(
      switchMap(emailExists => {
        if (emailExists) {
          return of({ success: false, message: 'Email already registered!' });
        }
        return this.checkMobileExists(userData.mobile);
      }),
      switchMap(mobileExists => {
        if (mobileExists) {
          return of({ success: false, message: 'Mobile number already registered!' });
        }

        // Proceed with Firebase registration
        return from(createUserWithEmailAndPassword(this.auth, userData.email, userData.password)).pipe(
          switchMap(async (userCredential) => {
            const firebaseUser = userCredential.user;

            // Update Firebase Auth profile
            await updateFirebaseProfile(firebaseUser, {
              displayName: userData.name
            });

            // Create user document in Firestore with all required fields including password
            const newUser: any = {
              name: userData.name,
              email: userData.email,
              mobile: userData.mobile,
              password: userData.password, // Store password in database
              gender: userData.gender,
              currentWeight: userData.currentWeight,
              currentHeight: userData.currentHeight,
              dateOfBirth: userData.dateOfBirth,
              fullAddress: userData.fullAddress,
              altMobile: userData.altMobile || '',
              registrationDate: new Date(), // Current timestamp
              recentPhoto: typeof userData.recentPhoto === 'string' ? userData.recentPhoto : '',
              role: 'user', // Default to 'user' for new registrations
              takenDose: 0, // Default to 0 for new users
              completedDoses: [],
              currentDoseHistory: []
            };

            await setDoc(doc(this.firestore, 'users', firebaseUser.uid), newUser);

            const offline = (userData as any).offlinePurchaseData as OfflineDoseEntry[] | undefined;
            if (offline && offline.length > 0) {
              const orderId = await this.createOfflineOrderAndDoseHistory(
                firebaseUser.uid,
                offline
              );
              if (orderId) {
                const { history, completedDoses, takenDose } = this.buildOfflineDoseHistory(
                  offline,
                  orderId
                );
                await updateDoc(doc(this.firestore, 'users', firebaseUser.uid), {
                  currentDoseHistory: history,
                  completedDoses,
                  takenDose
                });
              }
            }

            // Don't set current user - registration should redirect to login
            await signOut(this.auth);

            return { success: true, message: 'Registration successful! Please login to continue.' };
          }),
          catchError((error) => {
            console.error('Registration error:', error);
            let message = 'Registration failed!';
            if (error.code === 'auth/email-already-in-use') {
              message = 'Email already registered!';
            } else if (error.code === 'auth/weak-password') {
              message = 'Password is too weak! Must be at least 6 characters.';
            } else if (error.code === 'auth/invalid-email') {
              message = 'Invalid email address!';
            }
            return of({ success: false, message });
          })
        );
      })
    );
  }

  // Login with email and password
  loginWithEmail(credentials: { email: string; password: string }): Observable<{ success: boolean; message: string; user?: User }> {
    return from(signInWithEmailAndPassword(this.auth, credentials.email, credentials.password)).pipe(
      switchMap(async (userCredential) => {
        const userData = await this.getUserData(userCredential.user.uid);
        if (userData) {
          this.currentUserSubject.next(userData);
          return { success: true, message: 'Login successful!', user: userData };
        }
        return { success: false, message: 'User data not found!' };
      }),
      catchError((error) => {
        console.error('Login error:', error);
        let message = 'Login failed!';
        if (error.code === 'auth/user-not-found') {
          message = 'No account found with this email!';
        } else if (error.code === 'auth/wrong-password') {
          message = 'Incorrect password!';
        } else if (error.code === 'auth/invalid-email') {
          message = 'Invalid email address!';
        } else if (error.code === 'auth/too-many-requests') {
          message = 'Too many failed attempts. Please try again later.';
        }
        return of({ success: false, message });
      })
    );
  }

  // Login with mobile and password
  loginWithMobilePassword(credentials: { mobile: string; password: string }): Observable<{ success: boolean; message: string; user?: User }> {
    // First find user by mobile number
    const usersRef = collection(this.firestore, 'users');
    const q = query(usersRef, where('mobile', '==', credentials.mobile));

    return from(getDocs(q)).pipe(
      switchMap(async (querySnapshot) => {
        if (querySnapshot.empty) {
          return { success: false, message: 'No account found with this mobile number!' };
        }

        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();

        // Check password (in production, you should hash passwords)
        if (userData['password'] !== credentials.password) {
          return { success: false, message: 'Incorrect password!' };
        }

        // Create user object
        const user: User = {
          id: userDoc.id,
          name: userData['name'] || '',
          email: userData['email'] || '',
          mobile: userData['mobile'] || '',
          password: '', // Don't return password
          gender: userData['gender'] || 'other',
          currentWeight: userData['currentWeight'] || 0,
          currentHeight: userData['currentHeight'] || 0,
          dateOfBirth: userData['dateOfBirth']?.toDate() || new Date(),
          fullAddress: userData['fullAddress'] || '',
          altMobile: userData['altMobile'] || '',
          registrationDate: userData['registrationDate']?.toDate() || new Date(),
          recentPhoto: userData['recentPhoto'] || '',
          role: userData['role'] || 'user', // Default to 'user' if no role specified
          takenDose: userData['takenDose'] || 0,
          completedDoses: userData['completedDoses'] || [],
          currentDoseHistory: userData['currentDoseHistory'] || []
        };

        this.currentUserSubject.next(user);
        return { success: true, message: 'Login successful!', user };
      }),
      catchError((error) => {
        console.error('Mobile login error:', error);
        return of({ success: false, message: 'Login failed! Please try again.' });
      })
    );
  }

  // Login with Google
  loginWithGoogle(): Observable<{ success: boolean; message: string; user?: User }> {
    const provider = new GoogleAuthProvider();
    return from(signInWithPopup(this.auth, provider)).pipe(
      switchMap(async (result) => {
        const firebaseUser = result.user;

        // Check if user already exists in Firestore
        let userData = await this.getUserData(firebaseUser.uid);

        if (!userData) {
          // Create new user document for Google sign-in
          const newUser: Omit<User, 'id' | 'password'> = {
            name: firebaseUser.displayName || '',
            email: firebaseUser.email || '',
            mobile: '',
            gender: 'other', // Default gender for Google sign-in
            currentWeight: 0,
            currentHeight: 0,
            dateOfBirth: new Date(),
            fullAddress: '',
            altMobile: '',
            registrationDate: new Date(),
            recentPhoto: firebaseUser.photoURL || '',
            role: 'user', // Default to 'user' for new Google sign-ins
            takenDose: 0, // Default to 0 for new users
            completedDoses: [],
            currentDoseHistory: []
          };

          await setDoc(doc(this.firestore, 'users', firebaseUser.uid), newUser);
          userData = { ...newUser, id: firebaseUser.uid, password: '' };
        }

        this.currentUserSubject.next(userData);
        return { success: true, message: 'Google login successful!', user: userData };
      }),
      catchError((error) => {
        console.error('Google login error:', error);
        return of({ success: false, message: 'Google login failed!' });
      })
    );
  }

  // Logout
  logout(): Observable<void> {
    return from(signOut(this.auth)).pipe(
      map(() => {
        this.currentUserSubject.next(null);
      }),
      catchError((error) => {
        console.error('Logout error:', error);
        return of();
      })
    );
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  // Check if user is logged in
  isLoggedIn(): Observable<boolean> {
    return this.currentUser$.pipe(
      map(user => user !== null)
    );
  }

  // Check if user is logged in synchronously
  isLoggedInSync(): boolean {
    return this.currentUserSubject.value !== null;
  }

  // Redirect URL management
  setRedirectUrl(url: string): void {
    this.redirectUrl = url;
  }

  getRedirectUrl(): string {
    const url = this.redirectUrl;
    this.redirectUrl = '/'; // Reset after getting
    return url;
  }

  // Update user profile
  updateUserProfile(updatedUser: Partial<User>): Observable<{ success: boolean; message: string }> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      return of({ success: false, message: 'User not logged in!' });
    }

    return from(updateDoc(doc(this.firestore, 'users', currentUser.id), updatedUser)).pipe(
      switchMap(async () => {
        // Get updated user data
        const userData = await this.getUserData(currentUser.id);
        if (userData) {
          this.currentUserSubject.next(userData);
        }
        return { success: true, message: 'Profile updated successfully!' };
      }),
      catchError((error) => {
        console.error('Profile update error:', error);
        return of({ success: false, message: 'Error updating profile!' });
      })
    );
  }

  // Check dose purchase eligibility
  checkDosePurchaseEligibility(userId: string): Observable<DosePurchaseEligibility> {
    return from(this.getUserData(userId)).pipe(
      map((user) => {
        if (!user) {
          return {
            canPurchaseDose1: false,
            canPurchaseDose2: false,
            canPurchaseDose3: false,
            canPurchaseCombo12: false,
            canPurchaseCombo23: false,
            canPurchaseCombo123: false,
            completedDoses: [],
            canPurchaseDoses: {
              1: false,
              2: false,
              3: false,
              4: false
            }
          };
        }

        const completedDoses = user.completedDoses;
        const hasGreen = completedDoses.includes('green');
        const hasYellow = completedDoses.includes('yellow');
        const hasRed = completedDoses.includes('red');

        // Find the last delivered dose (most recent startDate from dose history)
        // startDate represents the delivery date
        let lastDeliveryDate: Date | undefined = undefined;
        let daysSinceLastDelivery: number | undefined = undefined;
        let isCounterReset = false;

        if (user.currentDoseHistory && user.currentDoseHistory.length > 0) {
          // Find the most recent delivery date (startDate) from all dose history entries
          const allDeliveryDates: Date[] = [];
          
          user.currentDoseHistory.forEach((doseHistory: any) => {
            if (doseHistory.startDate) {
              let deliveryDate: Date;
              
              if (doseHistory.startDate && typeof (doseHistory.startDate as any).toDate === 'function') {
                // Firestore Timestamp
                deliveryDate = (doseHistory.startDate as any).toDate();
              } else if (doseHistory.startDate instanceof Date) {
                // Already a Date object
                deliveryDate = doseHistory.startDate;
              } else {
                // String or other format
                deliveryDate = new Date(doseHistory.startDate);
              }
              
              allDeliveryDates.push(deliveryDate);
            }
          });
          
          if (allDeliveryDates.length > 0) {
            // Get the most recent delivery date
            lastDeliveryDate = new Date(Math.max(...allDeliveryDates.map(d => d.getTime())));
            lastDeliveryDate.setHours(0, 0, 0, 0);
            
            // Calculate days since last delivery
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            const diffTime = today.getTime() - lastDeliveryDate.getTime();
            daysSinceLastDelivery = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            // If 40+ days have passed since last delivery, reset counter
            if (daysSinceLastDelivery >= 40) {
              isCounterReset = true;
            }
          }
        }

        // Check if any dose is currently active (in progress)
        // Only consider doses that haven't exceeded 30 days (active period)
        const activeDose: any = user.currentDoseHistory.find((h: any) => {
          if (h.status !== 'in_progress') return false;
          
          // Check if the dose is still within the 30-day active period
          let endDate: Date;
          if (h.endDate && typeof (h.endDate as any).toDate === 'function') {
            endDate = (h.endDate as any).toDate();
          } else if (h.endDate instanceof Date) {
            endDate = h.endDate;
          } else {
            endDate = new Date(h.endDate);
          }
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const endDateMidnight = new Date(endDate);
          endDateMidnight.setHours(0, 0, 0, 0);
          
          // Only consider doses that are still have days remaining (within 30-day period)
          return endDateMidnight >= today;
        });
        
        let currentActiveDose = undefined;
        if (activeDose) {
          // Convert Firestore Timestamp to JS Date if needed
          let endDate: Date;
          let startDate: Date;
          
          if (activeDose.endDate && typeof (activeDose.endDate as any).toDate === 'function') {
            // Firestore Timestamp
            endDate = (activeDose.endDate as any).toDate();
          } else if (activeDose.endDate instanceof Date) {
            // Already a Date object
            endDate = activeDose.endDate;
          } else {
            // String or other format
            endDate = new Date(activeDose.endDate);
          }
          
          if (activeDose.startDate && typeof (activeDose.startDate as any).toDate === 'function') {
            // Firestore Timestamp
            startDate = (activeDose.startDate as any).toDate();
          } else if (activeDose.startDate instanceof Date) {
            // Already a Date object
            startDate = activeDose.startDate;
          } else {
            // String or other format
            startDate = new Date(activeDose.startDate);
          }
          
          // Calculate days remaining from endDate (which is startDate + 30 days = deliveredDate + 30 days)
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Reset time to midnight for accurate day calculation
          
          const endDateMidnight = new Date(endDate);
          endDateMidnight.setHours(0, 0, 0, 0);
          
          const daysRemaining = Math.ceil((endDateMidnight.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          // Ensure daysRemaining is not negative
          const safeDaysRemaining = Math.max(0, daysRemaining);
          
          currentActiveDose = {
            doseType: activeDose.doseType,
            endDate: activeDose.endDate,
            startDate: activeDose.startDate, // Include startDate for verification
            orderId: activeDose.orderId, // Include orderId to check delivery status
            daysRemaining: safeDaysRemaining
          };
        }

        // If counter is reset (40+ days passed), allow dose 1 purchase
        // Otherwise, follow normal sequential logic
        const canBuyDose1 = isCounterReset ? true : (!hasGreen && !activeDose);
        const canBuyDose2 = isCounterReset ? false : (hasGreen && !hasYellow && !activeDose);
        const canBuyDose3 = isCounterReset ? false : (hasGreen && hasYellow && !hasRed && !activeDose);

        return {
          canPurchaseDose1: canBuyDose1,
          canPurchaseDose2: canBuyDose2,
          canPurchaseDose3: canBuyDose3,
          canPurchaseCombo12: isCounterReset ? true : (!hasGreen && !hasYellow && !activeDose),
          canPurchaseCombo23: isCounterReset ? false : (hasGreen && !hasYellow && !hasRed && !activeDose),
          canPurchaseCombo123: isCounterReset ? true : (!hasGreen && !hasYellow && !hasRed && !activeDose),
          completedDoses,
          currentActiveDose,
          canPurchaseDoses: {
            1: canBuyDose1,
            2: canBuyDose2,
            3: canBuyDose3,
            // Dose 4 (e.g., Liquid Formula) unlocks only after completing 1-3
            4: isCounterReset ? false : (hasGreen && hasYellow && hasRed && !activeDose)
          },
          daysSinceLastDelivery,
          isCounterReset,
          lastDeliveryDate
        };
      })
    );
  }

  private readonly ORDERS_COLLECTION = 'orderTable';

  private async createOfflineOrderAndDoseHistory(
    userId: string,
    doses: OfflineDoseEntry[]
  ): Promise<string | null> {
    try {
      const orderDate = new Date(
        Math.max(...doses.map(d => new Date(d.purchaseDate).getTime()))
      );
      const products = doses.map(d => ({
        doseType: d.doseType,
        doseNumber: d.doseNumber,
        purchaseDate: new Date(d.purchaseDate),
        name: `Dose ${d.doseNumber} (${d.doseType})`,
        id: `offline-${d.doseType}`
      }));
      const doseTypes = doses.map(d => d.doseType);
      const orderData = {
        userId,
        products,
        totalAmount: 0,
        discountAmount: 0,
        deliveryCharges: 0,
        status: 'delivered',
        orderDate,
        deliveryAddress: 'Offline purchase',
        paymentMethod: 'offline',
        paymentStatus: 'completed',
        doseTypes,
        isComboOrder: false,
        isOfflinePurchase: true,
        deliveredDate: orderDate,
        orderHistory: [{
          timestamp: new Date(),
          status: 'delivered',
          notes: 'Offline purchase recorded during registration',
          updatedBy: 'system'
        }]
      };
      const ordersRef = collection(this.firestore, this.ORDERS_COLLECTION);
      const docRef = await addDoc(ordersRef, orderData);
      return docRef.id;
    } catch (e) {
      console.error('createOfflineOrderAndDoseHistory error:', e);
      return null;
    }
  }

  private buildOfflineDoseHistory(
    doses: OfflineDoseEntry[],
    orderId: string
  ): { history: any[]; completedDoses: DoseType[]; takenDose: number } {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const history: any[] = [];
    const completedDoses: DoseType[] = [];
    let takenDose = 0;
    const typeToNum: Record<string, number> = { green: 1, yellow: 2, red: 3 };

    for (const d of doses) {
      const startDate = new Date(d.purchaseDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 30);
      const isCompleted = endDate < today;
      if (isCompleted && !completedDoses.includes(d.doseType as DoseType)) {
        completedDoses.push(d.doseType as DoseType);
      }
      const num = typeToNum[d.doseType];
      if (num && num > takenDose) takenDose = num;
      history.push({
        id: `offline-${orderId}-${d.doseType}-${Date.now()}`,
        userId: '', // Not needed in stored doc for update
        doseType: d.doseType,
        doseNumber: d.doseNumber,
        orderId,
        startDate,
        endDate,
        status: isCompleted ? 'completed' : 'in_progress',
        purchaseDate: new Date(d.purchaseDate)
      });
    }
    return { history, completedDoses, takenDose };
  }

  // Check if email exists
  checkEmailExists(email: string): Observable<boolean> {
    const usersRef = collection(this.firestore, 'users');
    const q = query(usersRef, where('email', '==', email));
    return from(getDocs(q)).pipe(
      map(querySnapshot => !querySnapshot.empty)
    );
  }

  // Check if mobile exists
  checkMobileExists(mobile: string): Observable<boolean> {
    const usersRef = collection(this.firestore, 'users');
    const q = query(usersRef, where('mobile', '==', mobile));
    return from(getDocs(q)).pipe(
      map(querySnapshot => !querySnapshot.empty)
    );
  }

  verifyUserForPasswordReset(details: { mobile: string; email: string; dob: string }): Observable<{ success: boolean; message: string; userId?: string }> {
    const usersRef = collection(this.firestore, 'users');
    const q = query(usersRef, where('mobile', '==', details.mobile));

    return from(getDocs(q)).pipe(
      map(querySnapshot => {
        if (querySnapshot.empty) {
          return { success: false, message: 'No account found with this mobile number.' };
        }

        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();

        if ((userData['email'] || '').toLowerCase() !== details.email.toLowerCase()) {
          return { success: false, message: 'Provided details do not match our records.' };
        }

        const storedDobRaw = userData['dateOfBirth'];
        let storedDob: Date | null = null;
        if (storedDobRaw) {
          if (typeof (storedDobRaw as any).toDate === 'function') {
            storedDob = (storedDobRaw as any).toDate();
          } else if (storedDobRaw instanceof Date) {
            storedDob = storedDobRaw;
          } else {
            storedDob = new Date(storedDobRaw);
          }
        }

        if (!storedDob) {
          return { success: false, message: 'Provided details do not match our records.' };
        }

        const inputDob = new Date(details.dob);
        const normalize = (d: Date) => {
          const nd = new Date(d);
          nd.setHours(0, 0, 0, 0);
          return nd.getTime();
        };

        if (normalize(storedDob) !== normalize(inputDob)) {
          return { success: false, message: 'Provided details do not match our records.' };
        }

        return { success: true, message: 'User verified successfully.', userId: userDoc.id };
      }),
      catchError((error) => {
        console.error('verifyUserForPasswordReset error:', error);
        return of({ success: false, message: 'Failed to verify user details.' });
      })
    );
  }

  resetPasswordForVerifiedUser(userId: string, newPassword: string): Observable<{ success: boolean; message: string }> {
    return from(updateDoc(doc(this.firestore, 'users', userId), { password: newPassword })).pipe(
      map(() => {
        return { success: true, message: 'Password reset successfully.' };
      }),
      catchError((error) => {
        console.error('resetPasswordForVerifiedUser error:', error);
        return of({ success: false, message: 'Failed to reset password.' });
      })
    );
  }

  // Add dose to user's history
  addDoseToHistory(userId: string, doseHistory: Omit<UserDoseHistory, 'id' | 'userId'>): Observable<{ success: boolean; message: string }> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      return of({ success: false, message: 'User not logged in!' });
    }

    // Generate a unique ID for the dose history entry
    const historyEntry: UserDoseHistory = {
      ...doseHistory,
      id: Date.now().toString(),
      userId: userId
    };

    // Update the user's currentDoseHistory and completedDoses
    const updatedData: Partial<User> = {
      currentDoseHistory: [...(currentUser.currentDoseHistory || []), historyEntry]
    };

    // Determine the current dose number based on dose type
    let currentDoseNumber = currentUser.takenDose || 0;
    
    // Map dose types to numbers
    const doseTypeToNumber: { [key in DoseType]: number } = {
      'green': 1,
      'yellow': 2, 
      'red': 3
    };
    
    const newDoseNumber = doseTypeToNumber[doseHistory.doseType];
    if (newDoseNumber && newDoseNumber > currentDoseNumber) {
      updatedData.takenDose = newDoseNumber;
      currentDoseNumber = newDoseNumber;
      console.log(`🔄 Updating user takenDose from ${currentUser.takenDose} to ${newDoseNumber} for dose type: ${doseHistory.doseType}`);
    }

    // If this is a completed dose, add it to completedDoses array
    if (doseHistory.status === 'completed') {
      const completedDoses = currentUser.completedDoses || [];
      if (!completedDoses.includes(doseHistory.doseType)) {
        updatedData.completedDoses = [...completedDoses, doseHistory.doseType];
      }
    }

    return from(updateDoc(doc(this.firestore, 'users', userId), updatedData)).pipe(
      switchMap(async () => {
        // Get updated user data
        const userData = await this.getUserData(userId);
        if (userData) {
          this.currentUserSubject.next(userData);
        }
        return { success: true, message: 'Dose history updated successfully!' };
      }),
      catchError((error) => {
        console.error('Add dose to history error:', error);
        return of({ success: false, message: 'Error updating dose history!' });
      })
    );
  }

  // Send OTP to phone number
  sendOTP(phoneNumber: string, recaptchaVerifier: RecaptchaVerifier): Observable<{ success: boolean; message: string; confirmationResult?: ConfirmationResult }> {
    // Format phone number to international format
    const formattedPhoneNumber = phoneNumber.startsWith('+91') ? phoneNumber : `+91${phoneNumber}`;

    console.log('Firebase sendOTP - Original number:', phoneNumber);
    console.log('Firebase sendOTP - Formatted number:', formattedPhoneNumber);
    console.log('Firebase sendOTP - RecaptchaVerifier:', recaptchaVerifier);

    return from(signInWithPhoneNumber(this.auth, formattedPhoneNumber, recaptchaVerifier)).pipe(
      map((confirmationResult) => {
        return {
          success: true,
          message: 'OTP sent successfully!',
          confirmationResult
        };
      }),
      catchError((error) => {
        console.error('Send OTP error:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        
        let message = 'Failed to send OTP!';
        if (error.code === 'auth/invalid-phone-number') {
          message = 'Invalid phone number!';
        } else if (error.code === 'auth/too-many-requests') {
          message = 'Too many OTP attempts. Please wait 1-2 hours before trying again or use a different phone number.';
        } else if (error.code === 'auth/captcha-check-failed') {
          message = 'Captcha verification failed. Please try again.';
        } else if (error.code === 'auth/invalid-app-credential') {
          message = 'Authentication setup error. Please refresh the page and try again.';
        } else if (error.code === 'auth/operation-not-allowed') {
          message = 'Phone authentication is not enabled. Please contact support.';
        }
        return of({ success: false, message });
      })
    );
  }

  // Verify OTP and login - only allow registered users
  verifyOTPAndLogin(confirmationResult: ConfirmationResult, otp: string, phoneNumber: string, name?: string): Observable<{ success: boolean; message: string; user?: User }> {
    return from(confirmationResult.confirm(otp)).pipe(
      switchMap(async (userCredential) => {
        const firebaseUser = userCredential.user;

        // Check if user is registered in our Firestore users collection
        const formattedPhoneNumber = phoneNumber.startsWith('+91') ? phoneNumber.slice(3) : phoneNumber;

        // First check by mobile number in Firestore
        const usersRef = collection(this.firestore, 'users');
        const q = query(usersRef, where('mobile', '==', formattedPhoneNumber));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          // User is not registered, sign them out and return error
          await signOut(this.auth);
          return { success: false, message: 'Mobile number not registered. Please register first.' };
        }

        // Get user data from Firestore
        let userData = await this.getUserData(firebaseUser.uid);

        if (!userData) {
          // If user exists by mobile but not by UID, update the user document with the new UID
          const userDoc = querySnapshot.docs[0];
          const existingUserData = userDoc.data();

          // Update the existing user document with the new Firebase UID
          // await setDoc(doc(this.firestore, 'users', firebaseUser.uid), existingUserData);

          // Remove the old document if it has different ID
          // if (userDoc.id !== firebaseUser.uid) {
          // Note: In production, you might want to keep the old document or handle this differently
          // console.log('User document updated with new UID:', firebaseUser.uid);
          // }

          userData = {
            id: firebaseUser.uid,
            name: existingUserData['name'] || '',
            email: existingUserData['email'] || '',
            mobile: existingUserData['mobile'] || '',
            password: '',
            gender: existingUserData['gender'] || 'other',
            currentWeight: existingUserData['currentWeight'] || 0,
            currentHeight: existingUserData['currentHeight'] || 0,
            dateOfBirth: existingUserData['dateOfBirth']?.toDate() || new Date(),
            fullAddress: existingUserData['fullAddress'] || '',
            altMobile: existingUserData['altMobile'] || '',
            registrationDate: existingUserData['registrationDate']?.toDate() || new Date(),
            recentPhoto: existingUserData['recentPhoto'] || '',
            role: existingUserData['role'] || 'user', // Default to 'user' if no role specified
            takenDose: existingUserData['takenDose'] || 0,
            completedDoses: existingUserData['completedDoses'] || [],
            currentDoseHistory: existingUserData['currentDoseHistory'] || []
          };
        }

        if (userData) {
          this.currentUserSubject.next(userData);
          return { success: true, message: 'Login successful!', user: userData };
        } else {
          return { success: false, message: 'Failed to retrieve user data.' };
        }
      }),
      catchError((error) => {
        console.error('OTP verification error:', error);
        let message = 'Invalid OTP!';
        if (error.code === 'auth/invalid-verification-code') {
          message = 'Invalid verification code!';
        } else if (error.code === 'auth/code-expired') {
          message = 'OTP has expired. Please request a new one.';
        }
        return of({ success: false, message });
      })
    );
  }

  // Create reCAPTCHA verifier
  createRecaptchaVerifier(containerId: string): RecaptchaVerifier {
    try {
      const verifier = new RecaptchaVerifier(this.auth, containerId, {
        'size': 'normal',
        'callback': (response: any) => {
          console.log('reCAPTCHA solved:', response);
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
        }
      });
      
      // Render the widget
      verifier.render();
      return verifier;
    } catch (error) {
      console.error('Error creating reCAPTCHA verifier:', error);
      throw error;
    }
  }
}
